import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { getMyVacationsApi, MyVacationPeriod, MyVacationSchedule } from "../api/meApi";
import EmployeeShell from "../components/EmployeeShell";
import { COLABORADOR_HOME } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";

import "./Colaborador.css";

/** Data civil (meia-noite UTC no backend) — sempre em UTC, senão mostra o dia anterior. */
const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString("pt-BR", { timeZone: "UTC" }) : "—";

const PERIOD_STATUS: Record<MyVacationPeriod["status"], { label: string; tone: string }> = {
  EM_AQUISICAO: { label: "Em aquisição", tone: "colabBadge--gray" },
  DISPONIVEL: { label: "Disponível", tone: "colabBadge--green" },
  PARCIALMENTE_GOZADO: { label: "Programado", tone: "colabBadge--blue" },
  GOZADO: { label: "Gozado", tone: "colabBadge--gray" },
  VENCIDO: { label: "Vencido", tone: "colabBadge--orange" },
  PERDIDO: { label: "Sem direito", tone: "colabBadge--gray" },
};

const SCHEDULE_STATUS: Record<MyVacationSchedule["status"], string> = {
  PLANEJADO: "Planejado",
  AVISADO: "Confirmado",
  EM_GOZO: "Em férias",
  CONCLUIDO: "Concluído",
  CANCELADO: "Cancelado",
};

/** Períodos aquisitivos, saldo e férias programadas do próprio colaborador. Somente leitura. */
export default function MyVacationsPage() {
  const goBack = useGoBack(COLABORADOR_HOME());
  const [periods, setPeriods] = useState<MyVacationPeriod[] | null>(null);

  useEffect(() => {
    getMyVacationsApi()
      .then(setPeriods)
      .catch((error: any) => {
        toast.error(error?.message || "Não foi possível carregar as suas férias.");
        setPeriods([]);
      });
  }, []);

  return (
    <EmployeeShell>
      <section className="colabPanel">
        <div className="colabPageTitle">
          <h2 className="colabPanel__title" style={{ margin: 0 }}>
            Minhas férias
          </h2>
          <button type="button" className="colabBtn colabBtn--ghost" onClick={goBack}>
            Voltar
          </button>
        </div>

        {periods === null ? (
          <p className="colabLoading">Carregando...</p>
        ) : periods.length === 0 ? (
          <p className="colabHint">Nenhum período de férias encontrado.</p>
        ) : (
          <div className="colabList">
            {periods.map((period) => {
              const status = PERIOD_STATUS[period.status];
              return (
                <article key={period.id} className="colabListItem">
                  <div className="colabListItem__head">
                    <span>
                      Período {formatDate(period.acquisitionStart)} a {formatDate(period.acquisitionEnd)}
                    </span>
                    <span className={`colabBadge ${status.tone}`}>{status.label}</span>
                  </div>

                  {period.status === "EM_AQUISICAO" ? (
                    <p className="colabListItem__meta">
                      Você completa este período em {formatDate(period.acquisitionEnd)}.
                    </p>
                  ) : (
                    <>
                      <div>
                        <span className="colabBalance">{period.balance}</span>{" "}
                        <span className="colabListItem__meta">dia(s) de saldo, de {period.entitledDays}</span>
                      </div>
                      {period.balance > 0 ? (
                        <p className="colabListItem__meta">
                          As férias deste período devem ser concedidas até {formatDate(period.concessionEnd)}.
                        </p>
                      ) : null}
                    </>
                  )}

                  {period.schedules.map((schedule) => (
                    <p key={schedule.id} className="colabListItem__meta">
                      <strong>{SCHEDULE_STATUS[schedule.status]}:</strong> {formatDate(schedule.startDate)} a{" "}
                      {formatDate(schedule.endDate)} ({schedule.days} dias
                      {schedule.abonoDays > 0 ? ` + ${schedule.abonoDays} vendidos` : ""})
                      {schedule.thirteenthAdvance ? " · com adiantamento do 13º" : ""}
                    </p>
                  ))}
                </article>
              );
            })}
          </div>
        )}

        <p className="colabFooterNote">Para marcar ou mudar suas férias, fale com o Departamento Pessoal.</p>
      </section>
    </EmployeeShell>
  );
}
